import React, { useState, useEffect } from 'react';
import { Card, Form, Typography, Divider } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { LocationDTO } from '@/shared/api/model';
import LocationCascade from './LocationCascade';
import useLocation from '../hooks/useLocation';

const { Text } = Typography;

export interface LocationSelectorProps {
    title?: string;
    configName?: string;
    onLocationSelected?: (location: {
        zone?: LocationDTO;
        region?: LocationDTO;
        district?: LocationDTO;
        ward?: LocationDTO;
        street?: LocationDTO;
        fullPath?: string;
    }) => void;
    required?: {
        zone?: boolean;
        region?: boolean;
        district?: boolean;
        ward?: boolean;
        street?: boolean;
    };
    zone?: boolean;
    region?: boolean;
    district?: boolean;
    ward?: boolean;
    street?: boolean;
    disabled?: boolean;
    initialValues?: {
        zoneId?: number;
        regionId?: number;
        districtId?: number;
        wardId?: number;
        streetId?: number;
    };
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
    title = "Select Location",
    configName,
    onLocationSelected,
    required = {},
    zone = true,
    region = true,
    district = true,
    ward = true,
    street = true,
    disabled = false,
    initialValues
}) => {
    const [fullPath, setFullPath] = useState<string>('');
    const { locationHierarchy } = useLocation();

    // Update the full path when location hierarchy changes
    useEffect(() => {
        if (locationHierarchy?.fullPath) {
            setFullPath(locationHierarchy.fullPath);
        }
    }, [locationHierarchy]);

    // Notify parent component about location selections
    const handleSelectionChange = (selections: {
        zone?: LocationDTO;
        region?: LocationDTO;
        district?: LocationDTO;
        ward?: LocationDTO;
        street?: LocationDTO;
    }) => {
        if (onLocationSelected) {
            onLocationSelected({
                ...selections,
                fullPath: fullPath || undefined
            });
        }
    };

    return (
        <Card 
            title={
                <div className="flex items-center">
                    <EnvironmentOutlined className="mr-2" />
                    <span>{title}</span>
                </div>
            }
            size="small"
            className="mb-4"
        >
            <Form layout="vertical">
                <LocationCascade
                    disabled={disabled}
                    showLabels={true}
                    required={required}
                    zone={zone}
                    region={region}
                    district={district}
                    ward={ward}
                    street={street}
                    onSelectionChange={handleSelectionChange}
                    initialValues={initialValues}
                />

                {fullPath && (
                    <>
                        <Divider className="my-2" />
                        <div className="text-sm">
                            <Text type="secondary">Full Path:</Text>
                            <div className="mt-1 text-gray-700 dark:text-gray-300">
                                {fullPath}
                            </div>
                        </div>
                    </>
                )}
            </Form>
        </Card>
    );
};

export default LocationSelector;
